// modules/Shop/ShopLogic.js
// 商店逻辑模块
import VehicleEquipmentSystem from '../VehicleEquipmentSystem.js';
import BlueprintSystem from '../BlueprintSystem.js';

export default class ShopLogic {
    constructor(scene, playerData) {
        this.scene = scene;
        this.playerData = playerData;
    }
    
    /**
     * 获取可出售的物品列表
     * @returns {Array} 可出售物品列表
     */
    getSellableItems() {
        if (!this.playerData.inventory || this.playerData.inventory.length === 0) {
            return [];
        }
        
        // 统计每个物品的数量
        const itemMap = new Map();
        this.playerData.inventory.forEach(item => {
            const key = item.id || item.name;
            if (!itemMap.has(key)) {
                itemMap.set(key, { item: item, count: 0 });
            }
            itemMap.get(key).count++;
        });
        
        // 转换为数组，过滤掉不可出售的物品（比如任务物品）
        const sellableItems = [];
        itemMap.forEach((itemData, key) => {
            // 可以出售的物品：有价格或可以计算价格的物品
            if (this.calculateSellPrice(itemData.item) > 0) {
                sellableItems.push(itemData);
            }
        });
        
        return sellableItems;
    }
    
    /**
     * 计算物品的出售价格
     * 通常是原价的50%，如果没有原价则根据物品类型和属性估算
     * @param {Object} item - 物品对象
     * @returns {number} 出售价格
     */
    calculateSellPrice(item) {
        // 如果有原价，按50%计算
        if (item.price) {
            return Math.floor(item.price * 0.5);
        }
        
        // 根据物品类型和属性估算价格
        let basePrice = 0;
        
        if (item.type === 'equipment' || item.slot) {
            // 装备类物品
            if (item.damage) {
                basePrice = item.damage * 10; // 攻击力 * 10
            }
            if (item.defense) {
                basePrice += item.defense * 5; // 防御力 * 5
            }
            if (basePrice === 0) {
                basePrice = 20; // 默认装备价格
            }
        } else if (item.type === 'vehicle_equipment') {
            // 战车装备
            if (item.damage) {
                basePrice = item.damage * 15; // 战车装备攻击力 * 15
            }
            if (item.defense) {
                basePrice += item.defense * 8; // 战车装备防御力 * 8
            }
            // 根据装备效果估算价格
            if (item.effects) {
                if (item.effects.spBonus) {
                    basePrice += item.effects.spBonus * 2;
                }
                if (item.effects.dodgeRate) {
                    basePrice += item.effects.dodgeRate * 1000;
                }
            }
            if (basePrice === 0) {
                basePrice = 50; // 默认战车装备价格
            }
        } else if (item.type === 'consumable') {
            // 消耗品
            if (item.healAmount) {
                basePrice = item.healAmount * 2; // 治疗效果 * 2
            } else if (item.damage) {
                basePrice = item.damage * 3; // 伤害 * 3
            } else if (item.repairAmount) {
                basePrice = item.repairAmount * 2; // 修理量 * 2
            } else {
                basePrice = 10; // 默认消耗品价格
            }
        } else {
            // 其他类型，默认价格
            basePrice = 10;
        }
        
        // 按50%计算出售价格
        return Math.floor(basePrice * 0.5);
    }
    
    /**
     * 执行购买
     * @param {Object} item - 要购买的物品
     * @returns {Object} 购买结果 {success: boolean, message: string}
     */
    executePurchase(item) {
        // 检查金钱是否足够
        if (this.playerData.money < item.price) {
            return {
                success: false,
                message: '金钱不足！',
                newMoney: this.playerData.money
            };
        }
        
        // 处理特殊物品（补给类、图纸类）
        if (item.type === 'blueprint') {
            // 图纸：检查是否已有该图纸
            if (BlueprintSystem.hasBlueprint(this.playerData, item.equipmentId)) {
                return {
                    success: false,
                    message: `你已经拥有${item.equipmentName}图纸了！`,
                    newMoney: this.playerData.money
                };
            }
            
            // 扣除金钱
            this.playerData.money -= item.price;
            
            // 添加图纸到背包
            const blueprint = BlueprintSystem.createBlueprint(item.equipmentId, item.equipmentName);
            if (!this.playerData.inventory) {
                this.playerData.inventory = [];
            }
            this.playerData.inventory.push(blueprint);
            
            return {
                success: true,
                message: `购买了${item.name}！`,
                newMoney: this.playerData.money
            };
        }
        
        if (item.type === 'ammo') {
            // 弹药：直接添加到战车
            if (!this.playerData.vehicle) {
                return {
                    success: false,
                    message: '没有战车！',
                    newMoney: this.playerData.money
                };
            }
            
            if (!this.playerData.vehicle.ammo) {
                this.playerData.vehicle.ammo = {};
            }
            
            const ammoType = item.ammoType || item.id;
            const amount = item.amount || 1;
            this.playerData.vehicle.ammo[ammoType] = (this.playerData.vehicle.ammo[ammoType] || 0) + amount;
            
            this.playerData.money -= item.price;
            
            return {
                success: true,
                message: `成功购买 ${item.name} x${amount}！`,
                newMoney: this.playerData.money
            };
        } else if (item.type === 'layer') {
            // 抗腐蚀图层：直接添加到战车
            if (!this.playerData.vehicle) {
                return {
                    success: false,
                    message: '没有战车！'
                };
            }
            
            // 使用 VehicleEquipmentSystem 计算最大图层值（从底盘获取）
            const maxLayer = VehicleEquipmentSystem.calculateMaxCorrosionLayer(this.playerData.vehicle);
            this.playerData.vehicle.maxCorrosionLayer = maxLayer; // 更新最大值
            
            // 如果没有底盘或底盘没有最大图层值，无法购买
            if (maxLayer === 0) {
                return {
                    success: false,
                    message: '没有装备底盘或底盘不支持抗腐蚀图层！',
                    newMoney: this.playerData.money
                };
            }
            
            const oldCorrosionLayer = this.playerData.vehicle.corrosionLayer || 0;
            
            // 如果已经达到上限，不扣除金钱
            if (oldCorrosionLayer >= maxLayer) {
                return {
                    success: false,
                    message: '抗腐蚀图层已满，无需补充！',
                    newMoney: this.playerData.money
                };
            }
            
            const amount = item.amount || 50;
            this.playerData.vehicle.corrosionLayer = Math.min(
                maxLayer,
                oldCorrosionLayer + amount
            );
            const actualLayerRestored = this.playerData.vehicle.corrosionLayer - oldCorrosionLayer;
            
            this.playerData.money -= item.price;
            
            return {
                success: true,
                message: `成功补充抗腐蚀图层 ${actualLayerRestored} 点！`,
                newMoney: this.playerData.money
            };
        } else if (item.type === 'sp') {
            // SP恢复：道具的amount代表重量，需要换算成SP（1SP=0.1重量）
            if (!this.playerData.vehicle) {
                return {
                    success: false,
                    message: '没有战车！',
                    newMoney: this.playerData.money
                };
            }
            
            // 使用VehicleEquipmentSystem计算SP最大值（基于载重）
            const maxSp = VehicleEquipmentSystem.calculateMaxSp(this.playerData.vehicle);
            this.playerData.vehicle.maxSp = maxSp; // 确保最大值正确
            
            const oldSp = this.playerData.vehicle.sp || 0;
            
            // 如果已经达到上限，不扣除金钱
            if (oldSp >= maxSp) {
                return {
                    success: false,
                    message: 'SP已满，无需恢复！',
                    newMoney: this.playerData.money
                };
            }
            
            const weightAmount = item.amount || 100; // 道具的amount是重量单位
            // 根据1SP=0.1重量换算：SP = 重量 / 0.1 = 重量 * 10
            const spAmount = weightAmount * 10;
            
            this.playerData.vehicle.sp = Math.min(
                maxSp,
                oldSp + spAmount
            );
            const actualSpRestored = this.playerData.vehicle.sp - oldSp;
            
            this.playerData.money -= item.price;
            
            return {
                success: true,
                message: `成功恢复SP ${actualSpRestored} 点！`,
                newMoney: this.playerData.money
            };
        }
        
        // 普通物品：添加到背包
        // 扣除金钱
        this.playerData.money -= item.price;
        
        // 添加到玩家背包
        const newItem = { ...item };
        delete newItem.price; // 移除价格信息
        
        if (!this.playerData.inventory) {
            this.playerData.inventory = [];
        }
        
        this.playerData.inventory.push(newItem);
        
        return {
            success: true,
            message: `成功购买 ${item.name}！`,
            newMoney: this.playerData.money
        };
    }
    
    /**
     * 执行出售
     * @param {Object} itemData - 要出售的物品数据 {item: Object, count: number}
     * @returns {Object} 出售结果 {success: boolean, message: string, newMoney: number}
     */
    executeSell(itemData) {
        const sellPrice = this.calculateSellPrice(itemData.item);
        const totalPrice = sellPrice * itemData.count;
        
        // 移除物品（移除所有相同物品）
        const itemId = itemData.item.id || itemData.item.name;
        let removedCount = 0;
        this.playerData.inventory = this.playerData.inventory.filter(item => {
            const currentId = item.id || item.name;
            if (currentId === itemId && removedCount < itemData.count) {
                removedCount++;
                return false; // 移除
            }
            return true; // 保留
        });
        
        // 增加金钱
        this.playerData.money += totalPrice;
        
        return {
            success: true,
            message: `成功出售 ${itemData.item.name} x${itemData.count}，获得 ${totalPrice} G！`,
            newMoney: this.playerData.money
        };
    }
    
    /**
     * 检查是否可以购买
     * @param {Object} item - 物品对象
     * @returns {boolean} 是否可以购买
     */
    canPurchase(item) {
        return this.playerData.money >= item.price;
    }
}

